import { ComponentType } from "react";
import { IconBaseProps } from "react-icons";
import {
  GiCrosshair,
  GiGroundbreaker,
  GiPositionMarker,
  GiSkullCrack,
} from "react-icons/gi";
import { TbTargetArrow } from "react-icons/tb";
import { FaGamepad } from "react-icons/fa";
import { MdBolt, MdMap } from "react-icons/md";
import { RiSwordFill } from "react-icons/ri";
import { BiTimer } from "react-icons/bi";
import { ImFire } from "react-icons/im";
import { WEAPON_ICON_MAP } from "MVVM/Views/TimelineEditor/constants";

type TClipTag = { key: string; value: string };

export type TTagBadge = {
  label: string;
  icon: ComponentType<IconBaseProps>;
  weight: number;
  featured?: boolean;
  ace?: boolean;
  highlight?: boolean;
  iconUrl?: string;
  tooltip?: string;
};

const WEAPON_DISPLAY_NAMES: Record<string, string> = {
  "desert eagle": "DEAGLE",
  "he grenade": "HE GRENADE",
  "smoke grenade": "SMOKE",
  "incendiary grenade": "INCENDIARY",
  "decoy grenade": "DECOY",
  "dual berettas": "DUALIES",
  "r8 revolver": "R8",
  "zeus x27": "ZEUS",
};

const getWeaponIconUrl = (weapon: string): string => {
  const normalized = weapon.toLowerCase().trim();
  const iconName =
    WEAPON_ICON_MAP[normalized] || normalized.replace(/[\s-]/g, "");
  const { REACT_APP_S3_CLIP_BUCKET } = process.env;
  return `${REACT_APP_S3_CLIP_BUCKET}/static/web/cs2_icons/equipment/${iconName}.svg`;
};

const CS_KILL_COUNT_BADGES: Record<string, TTagBadge> = {
  "5": {
    label: "ACE",
    icon: ImFire,
    weight: 100,
    featured: true,
    ace: true,
    tooltip: "Killed all five players on the enemy team",
  },
  "4": {
    label: "4 KILLS",
    icon: RiSwordFill,
    weight: 90,
    featured: true,
    tooltip: "Killed 4 players in a single round",
  },
  "3": {
    label: "3 KILLS",
    icon: RiSwordFill,
    weight: 80,
    featured: true,
    tooltip: "Killed 3 players in a single round",
  },
};

const CS_SITUATIONAL_BADGES: Record<string, TTagBadge> = {
  quickscope: {
    label: "QUICKSCOPE",
    icon: MdBolt,
    weight: 85,
    featured: true,
    tooltip: "Got a quickscope kill",
  },
  noscope: {
    label: "NOSCOPE",
    icon: MdBolt,
    weight: 85,
    featured: true,
    tooltip: "Got a noscope kill",
  },
  wallbang: {
    label: "WALLBANG",
    icon: GiGroundbreaker,
    weight: 15,
    featured: true,
    tooltip: "Got a kill through a wall",
  },
};

const getCSBadges = (tags: TClipTag[]): TTagBadge[] => {
  const badges: TTagBadge[] = [];
  const tagMap = new Map(tags.map((t) => [t.key, t.value]));

  const killCount = tagMap.get("CS_Kill Count");
  const headshotCount = tagMap.get("CS_Headshots");

  if (killCount) {
    const badge = CS_KILL_COUNT_BADGES[killCount];
    if (badge) badges.push(badge);
  }

  if (headshotCount) {
    const count = parseInt(headshotCount, 10);
    const kills = killCount ? parseInt(killCount, 10) : 0;

    if (count === kills && kills > 1) {
      badges.push({
        label: "ALL HEADSHOTS",
        icon: GiSkullCrack,
        weight: 95,
        featured: true,
        highlight: true,
        tooltip: "Every kill was a headshot",
      });
    } else if (count >= 3) {
      badges.push({
        label: `${count} HEADSHOTS`,
        icon: TbTargetArrow,
        weight: 88,
        featured: true,
        tooltip: `Landed ${count} headshots`,
      });
    }
  }

  for (const tag of tags) {
    if (tag.key === "CS_Situational") {
      const badge = CS_SITUATIONAL_BADGES[tag.value.toLowerCase()];
      if (badge) badges.push(badge);
    }

    if (tag.key === "CS_Weapons") {
      const normalized = tag.value.toLowerCase().trim();
      const displayName =
        WEAPON_DISPLAY_NAMES[normalized] || tag.value.toUpperCase();
      badges.push({
        label: displayName,
        icon: GiCrosshair,
        weight: 10,
        iconUrl: getWeaponIconUrl(tag.value),
        tooltip: `Used ${displayName}`,
      });
    }
  }

  const map = tagMap.get("CS_Map");
  if (map) {
    badges.push({
      label: map.toUpperCase(),
      icon: MdMap,
      weight: 5,
      tooltip: `Played on ${map}`,
    });
  }

  const round = tagMap.get("CS_Round");
  if (round) {
    badges.push({
      label: `ROUND ${round}`,
      icon: BiTimer,
      weight: 5,
      tooltip: `Round ${round} of the match`,
    });
  }

  return badges;
};

const LEAGUE_MULTI_KILL_BADGES: Record<string, TTagBadge> = {
  "penta kill": {
    label: "PENTA KILL",
    icon: ImFire,
    weight: 100,
    featured: true,
    ace: true,
    tooltip: "Killed all five players on the enemy team",
  },
  "quadra kill": {
    label: "QUADRA KILL",
    icon: RiSwordFill,
    weight: 95,
    featured: true,
    tooltip: "Killed 4 players in rapid succession",
  },
  "triple kill": {
    label: "TRIPLE KILL",
    icon: RiSwordFill,
    weight: 90,
    featured: true,
    tooltip: "Killed 3 players in rapid succession",
  },
  "double kill": {
    label: "DOUBLE KILL",
    icon: RiSwordFill,
    weight: 80,
    tooltip: "Killed 2 players in rapid succession",
  },
};

const LEAGUE_POSITION_MAP: Record<string, string> = {
  middle: "MID",
  top: "TOP",
  bottom: "BOT",
  utility: "SUPPORT",
  jungle: "JUNGLE",
};

const getLeagueBadges = (tags: TClipTag[]): TTagBadge[] => {
  const badges: TTagBadge[] = [];
  const tagMap = new Map(tags.map((t) => [t.key, t.value]));

  const killCount = tagMap.get("LEAGUE_Kill Count");
  const multiKill = tagMap.get("LEAGUE_Multi Kill");
  const champion = tagMap.get("LEAGUE_Champion");

  if (multiKill) {
    const badge = LEAGUE_MULTI_KILL_BADGES[multiKill.toLowerCase()];
    if (badge) badges.push(badge);
  } else if (killCount === "5") {
    badges.push(LEAGUE_MULTI_KILL_BADGES["penta kill"]);
  } else if (killCount) {
    const count = parseInt(killCount, 10);
    if (count >= 3) {
      badges.push({
        label: `${count} KILLS`,
        icon: RiSwordFill,
        weight: 90,
        featured: true,
        tooltip: `Killed ${count} players`,
      });
    }
  }

  if (champion) {
    badges.push({
      label: champion.toUpperCase(),
      icon: FaGamepad,
      weight: 20,
      tooltip: `Played as ${champion}`,
    });
  }

  const matchType = tagMap.get("LEAGUE_Match Type");
  if (matchType) {
    badges.push({
      label: matchType.toUpperCase(),
      icon: MdMap,
      weight: 5,
      tooltip: `Match type: ${matchType}`,
    });
  }

  const position = tagMap.get("LEAGUE_Position");
  if (position) {
    const roleName =
      LEAGUE_POSITION_MAP[position.toLowerCase()] || position.toUpperCase();
    badges.push({
      label: roleName,
      icon: GiPositionMarker,
      weight: 10,
      tooltip: `Played ${roleName} Lane`,
    });
  }

  return badges;
};

export const getTagBadges = (tags?: TClipTag[]): TTagBadge[] => {
  if (!tags?.length) return [];

  const hasCS = tags.some((t) => t.key.startsWith("CS_"));
  const hasLeague = tags.some((t) => t.key.startsWith("LEAGUE_"));

  let badges: TTagBadge[] = [];

  if (hasCS) {
    badges = getCSBadges(tags);
  } else if (hasLeague) {
    badges = getLeagueBadges(tags);
  }

  return badges.sort((a, b) => b.weight - a.weight);
};
